import { useState, useEffect, useCallback, useMemo } from "react";
import { CompanyAPI, type CompanyColorPreferences } from "@/services/api";
import { useCompanyId } from "@/context/CompanyContext";
import type { Theme } from "@/config/themes";

// Module-level store for promises to prevent multiple fetches for the same companyId
const fetchPromises = new Map<string, Promise<CompanyColorPreferences>>();
// Module-level store for resolved data for quick access
const resolvedDataCache = new Map<string, CompanyColorPreferences>();
// Track which company IDs have had their initial fresh fetch
const initialFreshFetchCompleted = new Set<string>();

/** Bump when tenant theme payloads change shape so clients refetch once. */
const COLOR_PREFS_CACHE_VERSION = 4;

function isValidColorPreferences(
  data: unknown
): data is CompanyColorPreferences {
  if (typeof data !== "object" || data === null) return false;
  const prefs = (data as CompanyColorPreferences).color_preferences;
  if (typeof prefs !== "object" || prefs === null) return false;
  return Array.isArray(prefs.themes) && prefs.themes.length > 0;
}

function readCachedColorPreferences(
  companyId: string
): CompanyColorPreferences | null {
  try {
    const raw = localStorage.getItem(`companyColors_${companyId}`);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "v" in parsed &&
      (parsed as { v: number }).v === COLOR_PREFS_CACHE_VERSION &&
      "data" in parsed
    ) {
      const data = (parsed as { data: unknown }).data;
      if (!isValidColorPreferences(data)) {
        localStorage.removeItem(`companyColors_${companyId}`);
        return null;
      }
      return data;
    }
    // Legacy unversioned cache — drop so we refetch tenant themes
    localStorage.removeItem(`companyColors_${companyId}`);
    return null;
  } catch {
    return null;
  }
}

function writeCachedColorPreferences(
  companyId: string,
  data: CompanyColorPreferences
): void {
  try {
    localStorage.setItem(
      `companyColors_${companyId}`,
      JSON.stringify({ v: COLOR_PREFS_CACHE_VERSION, data })
    );
  } catch {
    /* Ignore localStorage errors */
  }
}

export interface UseCompanyColorsReturn {
  preferences: CompanyColorPreferences | null;
  themes: Theme[];
  workspaceThemesExclusive: boolean;
  chartColors: string[];
  colorPalette: string[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCompanyColors(): UseCompanyColorsReturn {
  const companyId = useCompanyId();

  const [preferences, setPreferences] =
    useState<CompanyColorPreferences | null>(() => {
      if (!companyId) return null;
      // 1. Check module-level resolved data cache only if initial fresh fetch was completed
      if (initialFreshFetchCompleted.has(companyId) && resolvedDataCache.has(companyId)) {
        return resolvedDataCache.get(companyId)!;
      }
      // 2. Check localStorage only as fallback while waiting for fresh fetch
      return readCachedColorPreferences(companyId);
    });

  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!companyId) return false;
    // If a fetch promise exists for this companyId, we are loading.
    if (fetchPromises.has(companyId)) return true;
    // Always loading initially if we haven't completed the fresh fetch
    if (!initialFreshFetchCompleted.has(companyId)) return true;
    // Otherwise, loading if preferences are not yet set
    return !preferences;
  });
  const [error, setError] = useState<string | null>(null);

  const fetchAndCachePreferences = useCallback(
    async (currentCompanyId: string, isRefetch: boolean = false, forceFresh: boolean = false) => {
      // If already fetching for this companyId (and not an explicit refetch), share the in-flight promise.
      // forceFresh only matters when there is NO existing promise — it just skips the resolved cache.
      if (!isRefetch && fetchPromises.has(currentCompanyId)) {
        setIsLoading(true);
        try {
          const data = await fetchPromises.get(currentCompanyId)!;
          // Ensure component is still mounted and interested in this companyId
          if (companyId === currentCompanyId) {
            setPreferences(data);
            setError(null);
          }
        } catch (err) {
          if (companyId === currentCompanyId) {
            setError(err instanceof Error ? err.message : "Unknown error");
            setPreferences(null);
          }
        } finally {
          if (companyId === currentCompanyId) {
            setIsLoading(false);
          }
        }
        return;
      }

      // Create and store the new fetch promise
      // setIsLoading(true) will be called before this function returns if a new promise is made.
      // This explicit call handles the start of the loading state for the instance initiating the fetch.
      if (companyId === currentCompanyId) {
        setIsLoading(true);
        setError(null); // Clear previous errors before a new fetch
      }

      const promise = CompanyAPI.getColorPreferences(currentCompanyId)
        .then((data) => {
          if (!isValidColorPreferences(data)) {
            throw new Error("Invalid company color preferences response");
          }
          resolvedDataCache.set(currentCompanyId, data);
          // Mark that we've completed the initial fresh fetch for this company
          initialFreshFetchCompleted.add(currentCompanyId);
          writeCachedColorPreferences(currentCompanyId, data);

          if (companyId === currentCompanyId) {
            setPreferences(data);
            setError(null);
          }
          return data;
        })
        .catch((err) => {
          console.error(
            `Error fetching company color preferences for ${currentCompanyId}:`,
            err
          );
          // Don't clear resolvedDataCache or localStorage on error, old data might be better than none
          // Or, clear them if fresh data is strictly required after an error:
          // resolvedDataCache.delete(currentCompanyId);
          // localStorage.removeItem(`companyColors_${currentCompanyId}`);

          if (companyId === currentCompanyId) {
            setError(err instanceof Error ? err.message : "Unknown error");
            setPreferences(null); // Clear preferences on error
          }
          throw err; // Rethrow for other awaiters or the refetch mechanism
        })
        .finally(() => {
          // Remove from in-flight ONLY after promise settles (either way)
          // This ensures subsequent calls correctly re-evaluate or start new fetch if needed.
          if (fetchPromises.get(currentCompanyId) === promise) {
            fetchPromises.delete(currentCompanyId);
          }
          if (companyId === currentCompanyId) {
            setIsLoading(false); // Ensure loading is false for this instance
          }
        });

      // Store the promise only if it's a new fetch (isRefetch or forceFresh might imply we want a new one anyway)
      // or if no promise is currently stored for this ID.
      if (!fetchPromises.has(currentCompanyId) || isRefetch || forceFresh) {
        fetchPromises.set(currentCompanyId, promise);
      }

      // If this instance is not the one that should manage the primary promise (e.g. another one already set it)
      // it should still await it. But the logic above should handle this.
      // This await ensures this instance's isLoading is correctly managed if it initiated the fetch.
      try {
        await promise;
      } catch {
        // Error handled in promise chain.
      }
    },
    [companyId]
  ); // companyId is the key dependency for the callback's closure

  useEffect(() => {
    if (!companyId) {
      setPreferences(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const cachedResolved = resolvedDataCache.get(companyId);
    if (cachedResolved && !isValidColorPreferences(cachedResolved)) {
      resolvedDataCache.delete(companyId);
      initialFreshFetchCompleted.delete(companyId);
      fetchPromises.delete(companyId);
    }

    // If we haven't completed the initial fresh fetch for this company, always fetch fresh
    if (!initialFreshFetchCompleted.has(companyId)) {
      fetchAndCachePreferences(companyId, false, true); // forceFresh = true
      return;
    }

    // If preferences are already loaded for the current companyId, and no fetch is in progress, bail.
    if (
      preferences &&
      resolvedDataCache.get(companyId) === preferences &&
      !fetchPromises.has(companyId)
    ) {
      setIsLoading(false); // Ensure loading is false if data is already there.
      setError(null);
      return;
    }

    // If data is in the module-level resolved cache (e.g., fetched by another instance)
    // and current state does not reflect it, update the state.
    if (resolvedDataCache.has(companyId)) {
      const dataFromGlobalCache = resolvedDataCache.get(companyId)!;
      if (preferences !== dataFromGlobalCache) {
        // Avoid unnecessary state update
        setPreferences(dataFromGlobalCache);
      }
      setIsLoading(false);
      setError(null);
      return;
    }

    // If no data in resolved cache, and no fetch promise exists, then initiate fetch.
    // fetchAndCachePreferences handles deduplication via fetchPromises map.
    fetchAndCachePreferences(companyId, false);
  }, [companyId, preferences, fetchAndCachePreferences]);

  const refetch = useCallback(async () => {
    if (!companyId) {
      // setError('Cannot refetch without a company ID.'); // Optional: provide user feedback
      return Promise.resolve();
    }

    // Indicate loading and clear previous error/data for this instance
    setIsLoading(true);
    setError(null);
    // Do not set preferences to null here, allow old data to show until new data arrives or error.
    // setPreferences(null);

    // Clear caches to force a new API call.
    resolvedDataCache.delete(companyId);
    localStorage.removeItem(`companyColors_${companyId}`);
    // Remove any existing promise for this ID to ensure a fresh call.
    fetchPromises.delete(companyId);
    // Reset the initial fresh fetch flag to ensure next usage will be fresh
    initialFreshFetchCompleted.delete(companyId);

    try {
      // The `true` for isRefetch in a more complex scenario could bypass the `fetchPromises.has` check,
      // but here, simply deleting from fetchPromises before calling is enough.
      // The main fetchAndCachePreferences will then create a new promise.
      await fetchAndCachePreferences(companyId, true); // Pass true for isRefetch
    } catch {
      // Error state (and isLoading=false) will be set by fetchAndCachePreferences's finally block
      // setError is already handled inside fetchAndCachePreferences for the current instance
    }
  }, [companyId, fetchAndCachePreferences]);

  // Convert company preferences to Theme format - memoized to prevent re-renders
  const themes: Theme[] = useMemo(
    () =>
      preferences?.color_preferences?.themes?.map((theme) => ({
        name: theme.name,
        colors: theme.colors,
        baseTextColor: theme.base_text_color,
        seed: theme.name.toLowerCase().replace(/\s+/g, ' '), // Use theme name as seed
      })) ?? [],
    [preferences]
  );

  // Extract chart colors - memoized
  const chartColors = useMemo(
    () => preferences?.color_preferences?.chart_colors?.primary_order ?? [],
    [preferences]
  );

  // Extract color palette - memoized
  const colorPalette = useMemo(
    () => preferences?.color_preferences?.color_selectors?.full_palette ?? [],
    [preferences]
  );

  const workspaceThemesExclusive = useMemo(
    () =>
      Boolean(
        preferences?.color_preferences?.usage_guidelines
          ?.workspace_themes_exclusive,
      ),
    [preferences],
  );

  return {
    preferences,
    themes,
    workspaceThemesExclusive,
    chartColors,
    colorPalette,
    isLoading,
    error,
    refetch: refetch, // Ensure the new refetch is returned
  };
}
